import { useState } from 'react'
import { Box, Button, Typography } from '@mui/material'
import { Close, QuestionMark, CompareArrows, Add } from '@mui/icons-material'

import { LoadingButton } from 'components/mui'

import useTrayAnalysisContentLogic from './TrayAnalysisContent.logic'
import { TrayType } from '../EditTrayModal/EditTrayModal.types'
import './TrayAnalysisContent.scss'
import TrayMapSelector from '../TrayMapSelector/TrayMapSelector'
import { OtherItemsTriggerButton } from 'components/organisms/OtherTrayItems/OtherTrayItemsTriggerButton'
import { useAddTrayContext } from 'views/TrayManagementLayout/AddTray/AddTray.context'
import CustomImplantModal from '../CustomImplantModal/CustomImplantModal'
import { useGrayScaleLogic } from '../GrayScale/GrayScale.logic'

import step4_4 from './step4-4.png'

const renderGuideline = (
  label: string,
  color: string,
  border: string,
  count: number
) => {
  return (
    <div className="legend-item">
      <span
        className="count-circle"
        style={{ backgroundColor: color, border: border }}
      >
        {count}
      </span>
      <Typography variant="body2">{label}</Typography>
    </div>
  )
}

const TrayAnalysisContent = ({
  onConfirm,
  onClose,
  trayType = 'stryker screw caddy',
  trayImg,
  selectedTrayType,
}: {
  onClose: () => void
  onConfirm: () => void
  trayType?: TrayType
  trayImg?: string
  selectedTrayType?: TrayType
}) => {
  const { templateData } = useAddTrayContext()
  const {
    shouldShowGuide,
    handleGuideClick,
    handleCustomImplantModalTrigger,
    isCustomImplantModalOpen,
    isCustomImplantTray,
  } = useTrayAnalysisContentLogic({ selectedTrayType })

  const [showAnalysisGuidance, setShowAnalysisGuidance] = useState(false)
  const {
    loadAllScrews,
    increaseAllPlatesBy,
    isAllScrewsLoaded,
    isAllPlatesLoaded,
    screwAnnotations,
    plateAnnotations,
    drillAnnotations,
  } = useGrayScaleLogic({
    trayType: selectedTrayType,
    templateData,
  })

  const toggleAnalysisGuidance = () => {
    setShowAnalysisGuidance(!showAnalysisGuidance)
  }

  return (
    <div className="loaner-tray-analysis-content_wrapper">
      <div className="header-section">
        <div className="header-content">
          <Box>
            <Typography variant="h4" lineHeight={1.2} className="main-title">
              Tray Content {isCustomImplantTray ? 'Analysis' : ''}
            </Typography>
            <Typography
              variant="body1"
              fontSize={14}
              textTransform="capitalize"
              className="tray-info"
            >
              Tray Type: <strong>{trayType}</strong>
            </Typography>
          </Box>
          <div className="header-actions">
            <LoadingButton
              className="help-button"
              variant="outlined"
              size="small"
              onClick={handleGuideClick}
            >
              {shouldShowGuide ? (
                <>
                  <Close fontSize="small" /> Hide Legend
                </>
              ) : (
                <>
                  <QuestionMark fontSize="small" /> Show Legend
                </>
              )}
            </LoadingButton>
          </div>
        </div>
      </div>
      {shouldShowGuide && (
        <div className="legend-banner">
          <div className="legend-content">
            {renderGuideline(
              'Empty Implant Hardware Position',
              'var(--empty-screw-background)',
              '0.5px solid #ddd',
              0
            )}
            {renderGuideline(
              'Loaded Implant Hardware Position',
              'var(--active-screw-background)',
              '0.5px solid transparent',
              1
            )}
          </div>
        </div>
      )}

      <div className="info-banner">
        <CompareArrows className="icon" />
        <Typography variant="body1">
          {isCustomImplantTray ? (
            <>
              <strong>Custom Implant Tray:</strong> Verify that you have placed
              all the custom implant product(s) from the individual packaging
              into your tray. Then index your image on the left to all the
              UDI(s) from the respective individual products/packages you have
              loaded using the smart tray capture feature (or enter manually).
            </>
          ) : (
            <>
              <strong>Compare:</strong> Review the captured tray image against
              the digital tray map to verify contents. You may adjust the tray
              map by tapping on the appopriate slot. This digital fingerprint
              will be utilized for invoicing.
            </>
          )}
        </Typography>
      </div>

      {showAnalysisGuidance && (
        <div className="analysis-guidance-panel">
          <img
            src={step4_4}
            alt="Analysis Guidance"
            className="analysis-guidance-image"
          />
        </div>
      )}

      <div className="content-section">
        <div className="analysis-panels">
          {trayImg && (
            <div className="panel image-panel">
              <div className="panel-header">
                <Typography variant="h6">Reference Image</Typography>
              </div>
              <div className="panel-content">
                <img
                  src={trayImg}
                  alt="Tray Reference"
                  className="tray-image"
                />
              </div>
            </div>
          )}

          <div className="panel map-panel">
            <div className="panel-header tray-map-header">
              <Typography variant="h6">Digital Tray Map</Typography>
              <div className="load-all-container">
                <OtherItemsTriggerButton trayType={trayType} />
                {isCustomImplantTray && (
                  <Button
                    variant="outlined"
                    startIcon={<Add />}
                    onClick={handleCustomImplantModalTrigger}
                  >
                    Add Custom Products
                  </Button>
                )}
                {screwAnnotations.length > 0 && !isCustomImplantTray && (
                  <Button
                    disabled={isAllScrewsLoaded}
                    onClick={loadAllScrews}
                    variant="outlined"
                  >
                    Load All Screws
                  </Button>
                )}
                {(plateAnnotations.length > 0 || drillAnnotations.length > 0) &&
                  !isCustomImplantTray && (
                    <Button
                      disabled={isAllPlatesLoaded}
                      onClick={() => increaseAllPlatesBy(1)}
                      variant="outlined"
                    >
                      Increase All Plates
                    </Button>
                  )}
              </div>
            </div>
            <div className="panel-content">
              <TrayMapSelector
                trayType={trayType}
                loadWithUDIs={false}
                trayImg={trayImg}
                templateData={templateData}
              />
            </div>
          </div>
        </div>
      </div>

      <CustomImplantModal
        open={isCustomImplantModalOpen}
        onClose={handleCustomImplantModalTrigger}
      />

      <div className="footer-section">
        {onClose && (
          <Button
            onClick={onClose}
            className="cancel-button"
            variant="text"
            color="primary"
          >
            Cancel
          </Button>
        )}
        <LoadingButton
          onClick={onConfirm}
          className="confirm-button"
          variant="contained"
          color="primary"
        >
          {isCustomImplantTray ? 'Confirm' : 'Confirm Analysis'}
        </LoadingButton>
      </div>
    </div>
  )
}

export default TrayAnalysisContent
